import { OrderWithValuation } from './AssetValuationList.data'
import {
    AppraiserAssignment,
    ContentChip,
    decimalToLocalizedString,
    getValueCheckResultChipColor,
    InfoStatus,
    isoDateStringToLocalizedMonthYear,
    isoDateStringToLocalFormat,
    MonetaryValue,
    TinyNavButton,
    tokens,
    useOrderStatus,
} from 'ComponentLibrary'
import { CircularProgress, Stack, styled } from '@mui/material'
import { IconButtonWithOptions, Typography } from 'ComponentLibrary/src/piazza'
import { TextNavButtonNew } from 'ComponentLibrary/src/piazza/buttons'
import { generatePath, Link as RouterLink } from 'react-router-dom'
import { MicrosoftExcelLogoIcon } from '@phosphor-icons/react'
import {
    ArrowUpRightIcon,
    CalendarDotIcon,
    CheckCircleIcon,
    DownloadSimpleIcon,
    FilesIcon,
    HighlighterIcon,
    XCircleIcon,
} from '@phosphor-icons/react'
import { useDownloadReport, useDownloadValXml } from 'ClientPortal/src/api/Queries/valuations'
import { DYNAMIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { useState } from 'react'
import { ValXmlDownloadModal } from 'ClientPortal/src/common/modals/ValXmlDownloadModal'
import { useTranslation } from 'react-i18next'
import { OrderStatus } from 'ComponentLibrary/src/common/order-status'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

const getDraftChipLabel = (title: string | undefined, draftLabel: string) => {
    const draftTitle = title?.split(' - ')[0]?.trim()
    if (!draftTitle) {
        return draftLabel
    }

    const draftVersion = draftTitle.match(/\d+/)?.[0]
    return draftVersion ? `${draftLabel} ${draftVersion}` : draftLabel
}

const getOrderStatusLabel = (status: OrderStatus, tCommon: (key: string) => string) => tCommon(`orderStatuses.${status}.label`)

export default function AssetValuationListItem({
    valuation,
    valueCheck,
    order,
    appraiserUsers,
    parentValuationStatus,
}: OrderWithValuation) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const { t: tAssetValuationListItem } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.assetValuationList.item' })
    const locale = useCurrentResolvedLocale()
    const { report_file, is_draft, status: valuationStatus, title, valuation_type, uuid: valuationId } = valuation ?? {}
    const isValueCheck = Boolean(valueCheck)
    const { uuid: order_uuid, status: orderStatus, valuation_date: orderValuationDate } = order ?? { status: 'done' }
    const valuationDate = valueCheck?.valuation_date ?? valuation?.valuation_date ?? orderValuationDate
    const valuationMonthAndYear = isoDateStringToLocalizedMonthYear(valuationDate, 'numeric', locale)
    const valuationTitle = isValueCheck
        ? tAssetValuationListItem('valueCheckTitle', { date: valuationMonthAndYear })
        : tAssetValuationListItem('valuationTitle', { date: valuationMonthAndYear })
    const hasValuationPage = isValueCheck || (valuation_type !== 'historical' && (is_draft || valuationStatus === 'finalised'))
    const formattedValuationDate = isoDateStringToLocalFormat(valuationDate, false, locale)

    const { getOrderStatusInfoVariant } = useOrderStatus()
    const { downloadReport, isDownloadingReport } = useDownloadReport(valuation?.asset_uuid ?? valueCheck?.asset_uuid)
    const { downloadValXml, isDownloadingValXml } = useDownloadValXml()
    const [isValXmlModalOpen, setIsValXmlModalOpen] = useState(false)

    const draftChipLabel = is_draft ? getDraftChipLabel(title, tCommon('draft')) : undefined
    const shouldShowPlausibilityButton =
        !!order && !order.external_participant_mailbox && !isValueCheck && is_draft && parentValuationStatus === 'in_review'
    const mortgageLendingValue = valuation?.mortgage_lending_value
    const marketValue = valueCheck?.market_value ?? valuation?.market_value
    const appraiserAssignment = valueCheck?.appraiser_assignment ?? valuation?.appraiser_assignment
    const externalAppraisingEntityName = valuation?.valuation_type !== 'einwert' ? valuation?.appraising_entity_name : undefined

    const handleValXmlDownload = async () => {
        if (!valuationId) {
            return
        }
        await downloadValXml({ valuationId })
        setIsValXmlModalOpen(false)
    }

    const isValuationValXMLExportable = valuation?.capabilities?.valxml_exportable.valxml_v1_1

    const valuationItem = (
        <ValuationItemContainer $clickable={hasValuationPage}>
            <Stack direction="column" gap={tokens.SizingSpacing050}>
                <Typography variant="headline3" color={hasValuationPage ? tokens.ColorTextLinkDefault : tokens.ColorTextDisabled} noWrap>
                    {valuationTitle}
                </Typography>
                <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={tokens.SizingSpacing050}>
                        <CalendarDotIcon
                            size={tokens.SizingIconS}
                            weight="bold"
                            color={formattedValuationDate ? tokens.ColorIconDefault : tokens.ColorIconDisabled}
                        />
                        <Typography
                            variant="textS"
                            color={formattedValuationDate ? tokens.ColorTextDefault : tokens.ColorTextDisabled}
                            noWrap
                        >
                            {formattedValuationDate ?? tCommon('noDate')}
                        </Typography>
                    </Stack>
                    {order !== undefined && (
                        <InfoStatus
                            variant={getOrderStatusInfoVariant(orderStatus)}
                            size="small"
                            label={getOrderStatusLabel(orderStatus, tCommon)}
                        />
                    )}
                    {isValueCheck ? (
                        <ContentChip size="small" color="blue" label={tCommon('valueCheck')} />
                    ) : is_draft ? (
                        <ContentChip size="small" highContrast color="orange" label={draftChipLabel} />
                    ) : valuationStatus === 'finalised' ? (
                        <ContentChip size="small" highContrast color="green" label={tCommon('final')} />
                    ) : undefined}
                    {order === undefined && <ContentChip size="small" color="grey" label={tCommon('imported')} />}
                </Stack>
            </Stack>

            <AppraiserOrderDateContainer>
                <AppraiserContainer>
                    <AppraiserAssignment
                        appraiserAssignment={appraiserAssignment}
                        externalAppraisingEntityName={externalAppraisingEntityName}
                        users={appraiserUsers}
                    />
                </AppraiserContainer>
                <PlausibilityContainer>
                    {shouldShowPlausibilityButton && (
                        <TextNavButtonNew
                            label={tActions('viewPlausibility')}
                            leftIcon={<HighlighterIcon />}
                            rightIcon={<ArrowUpRightIcon />}
                            variant="primary"
                            href={`/${generatePath(DYNAMIC_APP_PATHS.valuationPlausibilityPage, {
                                assetId: valuation?.asset_uuid,
                                valuationId,
                            })}`}
                        />
                    )}
                </PlausibilityContainer>
                <OrderContainer>
                    {order !== undefined && (
                        <TinyNavButton
                            leftIcon={<FilesIcon />}
                            rightIcon={<ArrowUpRightIcon />}
                            label={tActions('viewOrder')}
                            href={`/orders/${order_uuid}/overview`}
                        />
                    )}
                </OrderContainer>
            </AppraiserOrderDateContainer>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={tokens.SizingSpacing300} sx={{ gridColumn: '6 / 7' }}>
                {mortgageLendingValue && (
                    <Stack direction="column" alignItems="flex-end" gap={tokens.SizingSpacing025}>
                        <Typography variant="headline4" color={tokens.ColorTextBoldest} noWrap>
                            {MonetaryValue.fromMonetaryValueDataObject(mortgageLendingValue).format({ withoutDecimals: true, locale })}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault} noWrap>
                            {tExport('deviationAnalysis.deviationAnalysisRowMortgageLendingValue')}
                        </Typography>
                    </Stack>
                )}

                {valueCheck ? (
                    <Stack direction="column" gap={tokens.SizingSpacing050} alignItems="flex-end" justifyContent="center">
                        <ContentChip
                            size="small"
                            color={getValueCheckResultChipColor(valueCheck)}
                            label={
                                valueCheck.delta_market_value === undefined || valueCheck.delta_market_value === null
                                    ? '-'
                                    : `${decimalToLocalizedString(valueCheck.delta_market_value * 100, false, 1, 1, locale)} %`
                            }
                        />
                        <Stack direction="row" gap={tokens.SizingSpacing050} alignItems="center">
                            {valueCheck.confirmed ? (
                                <CheckCircleIcon size={tokens.SizingIconS} weight="fill" color={tokens.ColorIconPositive} />
                            ) : (
                                <XCircleIcon size={tokens.SizingIconS} weight="fill" color={tokens.ColorIconDanger} />
                            )}
                            <Typography variant="textS" color={tokens.ColorTextDefault} noWrap>
                                {valueCheck.confirmed ? tCommon('marketValueConfirmed') : tCommon('marketValueNotConfirmed')}
                            </Typography>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack direction="column" alignItems="flex-end" gap={tokens.SizingSpacing025}>
                        <Typography variant="headline4" color={tokens.ColorTextBoldest} noWrap>
                            {marketValue
                                ? MonetaryValue.fromMonetaryValueDataObject(marketValue).format({ withoutDecimals: true, locale })
                                : '- €'}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault} noWrap>
                            {tCommon('marketValue')}
                        </Typography>
                    </Stack>
                )}
            </Stack>
            <OptionsContainer>
                {isValueCheck ? (
                    isDownloadingReport ? (
                        <CircularProgress size={tokens.SizingIconS} sx={{ position: 'relative' }} />
                    ) : (
                        <IconButtonWithOptions
                            size="medium"
                            variant="tertiary"
                            tooltip={tCommon('options')}
                            options={[
                                {
                                    title: tAssetValuationListItem('downloadValueCheck'),
                                    icon: <DownloadSimpleIcon />,
                                    callback: () => downloadReport({ valueCheckId: valueCheck?.uuid }),
                                },
                            ]}
                        />
                    )
                ) : (
                    (is_draft || valuationStatus === 'finalised') &&
                    (isDownloadingReport ? (
                        <CircularProgress size={tokens.SizingIconS} sx={{ position: 'relative' }} />
                    ) : (
                        <IconButtonWithOptions
                            size="medium"
                            variant="tertiary"
                            tooltip={tCommon('options')}
                            options={[
                                {
                                    title: tAssetValuationListItem('generateValXml'),
                                    icon: <MicrosoftExcelLogoIcon />,
                                    callback: () => setIsValXmlModalOpen(true),
                                    disabled: !valuationId || isDownloadingValXml || !isValuationValXMLExportable,
                                    tooltip: !isValuationValXMLExportable ? tExport('valxmlUnavailableTooltip') : undefined,
                                },
                                {
                                    title: tAssetValuationListItem('downloadValuation'),
                                    icon: <DownloadSimpleIcon />,
                                    callback: () => downloadReport({ valuationId }),
                                    disabled: !report_file?.url,
                                    tooltip: !report_file?.url ? tAssetValuationListItem('noPdfTooltip') : undefined,
                                },
                            ]}
                        />
                    ))
                )}
            </OptionsContainer>
        </ValuationItemContainer>
    )

    const linkTo = valueCheck
        ? `/${generatePath(DYNAMIC_APP_PATHS.valueCheckDetailsPage, {
              assetId: valueCheck.asset_uuid,
              valueCheckId: valueCheck.uuid,
          })}`
        : hasValuationPage
          ? `${valuationId}/overview`
          : undefined

    if (linkTo) {
        return (
            <>
                <LinkContainer to={linkTo}>{valuationItem}</LinkContainer>
                <ValXmlDownloadModal
                    isOpen={isValXmlModalOpen}
                    onClose={() => setIsValXmlModalOpen(false)}
                    onDownload={handleValXmlDownload}
                    isDownloading={isDownloadingValXml}
                    mode="single"
                    isDraft={is_draft}
                />
            </>
        )
    }
    return (
        <>
            {valuationItem}
            <ValXmlDownloadModal
                isOpen={isValXmlModalOpen}
                onClose={() => setIsValXmlModalOpen(false)}
                onDownload={handleValXmlDownload}
                isDownloading={isDownloadingValXml}
                mode="single"
                isDraft={is_draft}
            />
        </>
    )
}

const ValuationItemContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $clickable }: { $clickable?: boolean }) => ({
    padding: tokens.SizingSpacing150,
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gridColumn: '1 / -1',
    borderRadius: tokens.SizingCornerL,
    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    backgroundColor: tokens.ColorSurfaceDefault,
    minHeight: '70px',
    '&:hover:not(:has(button:hover,a:hover))': {
        backgroundColor: $clickable ? tokens.ColorSurfaceHover : undefined,
    },
    '&:active:not(:has(button:hover,a:hover))': {
        backgroundColor: $clickable ? tokens.ColorSurfacePressed : undefined,
    },
}))

const AppraiserOrderDateContainer = styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gridColumn: '2 / 5',
}))

const AppraiserContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
}))

const PlausibilityContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}))

const OrderContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: tokens.SizingSpacing150,
    paddingLeft: tokens.SizingSpacing150,
}))

const OptionsContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    gridColumn: '7 / 8',
}))

const LinkContainer = styled(RouterLink)(() => ({
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gridColumn: '1 / -1',
    textDecoration: 'none',
}))
